import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import Header from '@/components/Header'
import { getApiSettings, updateApiSettings, updateApiKey, verifyCert, uploadCertPem, uploadKeyPem, getScenes, addScene, updateScene, deleteScene } from '../services/api'

interface ApiSettingsData {
  wechatMchId: string
  wechatApiKey: string
  wechatApiCertPath: string
  wechatApiKeyPath: string
  wechatApiSerial: string
  hasApiKey?: boolean
}

export default function ApiSettings() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [settings, setSettings] = useState<ApiSettingsData>({
    wechatMchId: '',
    wechatApiKey: '',
    wechatApiCertPath: '',
    wechatApiKeyPath: '',
    wechatApiSerial: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [certPem, setCertPem] = useState('')
  const [keyPem, setKeyPem] = useState('')
  const [sceneId, setSceneId] = useState<string>('1000')
  const [scenes, setScenes] = useState<Array<{ id: string | number; scene_id: string; name?: string; status?: string; user_recv_perception?: string; reportInfos?: Array<{ info_type: string; info_content: string }> }>>([])
  const [newScene, setNewScene] = useState<{ sceneId: '1000'|'1011'|'1002'|'1013'|'1004'|'1005'|'1009'|'1010'; name: string; status: 'active'|'disabled'; userRecvPerception?: string }>({ sceneId: '1009', name: '', status: 'active' })
  const [editingSceneId, setEditingSceneId] = useState<string | number | null>(null)
  const [editInfos, setEditInfos] = useState<Array<{ info_type: string; info_content: string }>>([])
  const [editPerception, setEditPerception] = useState<string>('')
  
  const ScenesInfo: Record<string, { title: string; perception: string; perceptions?: string[]; desc: string; required: Array<{ type: string; example: string }>; anyOf?: Array<{ type: string; example: string }>; optional: Array<{ type: string; example: string }> }> = {
    '1000': { title: '现金营销', perception: '活动奖励', perceptions: ['活动奖励','现金奖励'], desc: '需提供活动名称及奖励说明', required: [{ type: '活动名称', example: '新会员有礼' }, { type: '奖励说明', example: '注册会员抽奖一等奖' }], optional: [] },
    '1011': { title: '企业赔付', perception: '退款', perceptions: ['退款','商家赔付'], desc: '需描述赔付原因', required: [{ type: '赔付原因', example: '商品质量问题退款' }], optional: [] },
    '1005': { title: '佣金报酬', perception: '劳务报酬', perceptions: ['劳务报酬','报销款','企业补贴','开工利是'], desc: '需明确岗位及报酬说明', required: [{ type: '岗位类型', example: '外卖员' }, { type: '报酬说明', example: '7月份配送费' }], optional: [] },
    '1009': { title: '采购货款', perception: '货款', perceptions: ['货款'], desc: '需填写采购商品名称', required: [{ type: '采购商品名称', example: '戴尔笔记本电脑' }], optional: [] },
    '1010': { title: '二手回收', perception: '二手回收货款', perceptions: ['二手回收货款'], desc: '需注明回收商品', required: [{ type: '回收商品名称', example: '塑料瓶' }], optional: [] },
    '1013': { title: '公益补助', perception: '公益补助金', perceptions: ['公益补助金'], desc: '需提供公益活动名称与备案编号', required: [{ type: '公益活动名称', example: '在民政部的备案名称' }, { type: '公益活动备案编号', example: '在民政部的备案编号' }], optional: [] },
    '1002': { title: '行政补贴', perception: '行政补贴', perceptions: ['行政补贴','行政奖励'], desc: '需描述补贴类型', required: [{ type: '补贴类型', example: '购车补贴' }], optional: [] },
    '1004': { title: '保险理赔', perception: '保险理赔款', perceptions: ['保险理赔款'], desc: '需填写保险产品备案编号、名称及操作单号', required: [{ type: '保险产品备案编号', example: '01212121212' }, { type: '保险名称', example: '意外险' }, { type: '保险操作单号', example: '12121245442' }], optional: [] }
  }

  useEffect(() => {
    fetchSettings()
    fetchScenes()
  }, [user?.activeMerchantId])

  const fetchSettings = async () => {
    try {
      const response = await getApiSettings()
      const s = (response.data && (response.data.settings || response.data)) || {}
      setSettings({
        wechatMchId: s.wechatMchId || '',
        wechatApiKey: '',
        wechatApiCertPath: s.wechatApiCertPath || '',
        wechatApiKeyPath: s.wechatApiKeyPath || '',
        wechatApiSerial: s.wechatApiSerial || '',
        hasApiKey: !!s.hasApiKey
      })
    } catch (error) {
      console.error('获取API设置失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchScenes = async () => {
    try {
      const r = await getScenes()
      const list = (r.data?.scenes || []).map((s: any) => {
        let infos: Array<{ info_type: string; info_content: string }> | undefined
        try { infos = s.report_infos ? JSON.parse(s.report_infos) : undefined } catch {}
        return { ...s, reportInfos: infos }
      })
      setScenes(list)
    } catch {}
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)

    try {
      const payload = {
        wechatMchId: settings.wechatMchId,
        wechatApiCertPath: settings.wechatApiCertPath,
        wechatApiKeyPath: settings.wechatApiKeyPath,
        wechatApiSerial: settings.wechatApiSerial
      }
      await updateApiSettings(payload)
      await fetchSettings()
      alert('API设置保存成功')
    } catch (error) {
      alert('保存失败，请重试')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveApiKey = async () => {
    if (!settings.wechatApiKey || settings.wechatApiKey.length < 10) {
      alert('请输入有效的API密钥')
      return
    }
    try {
      await updateApiKey(settings.wechatApiKey)
      await fetchSettings()
      alert('API密钥保存成功')
      setSettings({ ...settings, wechatApiKey: '' })
    } catch (e: any) {
      alert(`API密钥保存失败：${e?.message || '请稍后重试'}`)
    }
  }

  const handleVerifyCert = async () => {
    try {
      await verifyCert()
      alert('证书校验成功')
    } catch {
      alert('证书校验失败')
    }
  }

  const handleInputChange = (field: keyof ApiSettingsData, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleUploadCertPem = async () => {
    if (!certPem || !certPem.includes('BEGIN CERTIFICATE')) {
      alert('请粘贴有效的证书PEM')
      return
    }
    try {
      const r = await uploadCertPem(certPem)
      const path = r.data?.path || settings.wechatApiCertPath
      setSettings(prev => ({ ...prev, wechatApiCertPath: path }))
      setCertPem('')
      alert('证书上传并保存成功')
    } catch (e) {
      alert('证书上传失败')
    }
  }

  const handleCertFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const f = e.target.files?.[0]
      if (!f) return
      const reader = new FileReader()
      reader.onload = () => {
        const text = String(reader.result || '')
        setCertPem(text)
      }
      reader.readAsText(f)
    } catch {
      alert('读取证书文件失败')
    }
  }

  const handleUploadKeyPem = async () => {
    if (!keyPem || !(keyPem.includes('BEGIN PRIVATE KEY') || keyPem.includes('BEGIN RSA PRIVATE KEY'))) {
      alert('请粘贴有效的私钥PEM')
      return
    }
    try {
      const r = await uploadKeyPem(keyPem)
      const path = r.data?.path || settings.wechatApiKeyPath
      setSettings(prev => ({ ...prev, wechatApiKeyPath: path }))
      setKeyPem('')
      alert('私钥上传并保存成功')
    } catch (e) {
      alert('私钥上传失败')
    }
  }

  const handleKeyFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const f = e.target.files?.[0]
      if (!f) return
      const reader = new FileReader()
      reader.onload = () => {
        const text = String(reader.result || '')
        setKeyPem(text)
      }
      reader.readAsText(f)
    } catch {
      alert('读取私钥文件失败')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="API设置" backTo="/dashboard" />

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg px-8 py-6">
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900">微信支付API配置</h2>
            <p className="mt-1 text-sm text-gray-500">
              配置微信支付商户API信息，确保转账功能正常运行
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wechat-green mx-auto"></div>
              <p className="mt-4 text-gray-500">加载中...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="mchId" className="block text-sm font-medium text-gray-700">
                  商户号 (MCH ID)
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="mchId"
                    value={settings.wechatMchId}
                    onChange={(e) => handleInputChange('wechatMchId', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                    placeholder="请输入微信支付商户号"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  在微信支付商户平台获取的商户号
                </p>
              </div>

                <div>
                  <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">API密钥</label>
                  <input
                    type="password"
                    id="apiKey"
                    value={settings.wechatApiKey}
                    onChange={(e) => handleInputChange('wechatApiKey', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                    placeholder="请输入API密钥"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  在微信支付商户平台设置的API密钥{settings.hasApiKey ? '（已配置，出于安全不显示原值）' : ''}
                </p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={handleSaveApiKey} className="bg-wechat-green hover:bg-wechat-dark text-white px-3 py-2 rounded-md text-sm font-medium">保存API密钥</button>
                  <button type="button" onClick={handleVerifyCert} className="bg-gray-800 hover:bg-black text-white px-3 py-2 rounded-md text-sm font-medium">证书校验</button>
                </div>
              

              <div>
                <label htmlFor="apiSerial" className="block text-sm font-medium text-gray-700">
                  API证书序列号
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="apiSerial"
                    value={settings.wechatApiSerial}
                    onChange={(e) => handleInputChange('wechatApiSerial', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                    placeholder="请输入API证书序列号"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  在微信支付商户平台上传的API证书序列号
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">API证书上传</label>
                <p className="mt-1 text-sm text-gray-500">粘贴证书PEM文本，系统将写入服务器安全目录</p>
                <input
                  type="file"
                  accept=".pem,.crt,.cer,.txt"
                  onChange={handleCertFileChange}
                  className="mt-2 text-sm"
                />
                <textarea
                  value={certPem}
                  onChange={(e) => setCertPem(e.target.value)}
                  rows={5}
                  placeholder={`-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----`}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md text-xs font-mono"
                />
                <div className="mt-2">
                  <button type="button" onClick={handleUploadCertPem} className="bg-gray-700 hover:bg-black text-white px-3 py-2 rounded-md text-sm font-medium">上传证书PEM</button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">API私钥上传</label>
                <p className="mt-1 text-sm text-gray-500">粘贴私钥PEM文本，系统将写入服务器安全目录</p>
                <input
                  type="file"
                  accept=".pem,.key,.txt"
                  onChange={handleKeyFileChange}
                  className="mt-2 text-sm"
                />
                <textarea
                  value={keyPem}
                  onChange={(e) => setKeyPem(e.target.value)}
                  rows={6}
                  placeholder={`-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----`}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md text-xs font-mono"
                />
                <div className="mt-2">
                  <button type="button" onClick={handleUploadKeyPem} className="bg-gray-700 hover:bg-black text-white px-3 py-2 rounded-md text-sm font-medium">上传私钥PEM</button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">回调通知地址</label>
                <p className="mt-1 text-sm text-gray-500">由系统提供并自动配置，无需填写</p>
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-xs text-gray-500 mb-1">系统生成的地址</div>
                  <div className="font-mono text-sm text-gray-900 break-all">
                    {typeof window !== 'undefined' ? `${window.location.origin}/api/wechat/transfer/callback` : ''}
                  </div>
                  {typeof window !== 'undefined' && (
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/api/wechat/transfer/callback`)}
                      className="mt-2 bg-gray-800 hover:bg-black text-white px-3 py-1 rounded-md text-xs font-medium"
                    >复制地址</button>
                  )}
                </div>
              </div>

              <div className="pt-5">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wechat-green"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-wechat-green hover:bg-wechat-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wechat-green disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? '保存中...' : '保存设置'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg px-6 py-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">配置说明</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 商户号和API密钥需要在微信支付商户平台获取</li>
            <li>• 支持直接在本页粘贴上传证书/私钥PEM，免环境变量</li>
            <li>• 回调地址由系统自动提供并为HTTPS，确保公网可访问</li>
            <li>• 修改配置后需要重启服务才能生效</li>
          </ul>
        </div>



        <div className="mt-6 bg-white border border-gray-200 rounded-lg px-6 py-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">商户转账场景管理</h3>
          <div className="mb-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select className="border rounded px-3 py-2" value={newScene.sceneId} onChange={(e)=>setNewScene({ ...newScene, sceneId: e.target.value as any })}>
                <option value="1000">1000（现金营销）</option>
                <option value="1011">1011（企业赔付）</option>
                <option value="1005">1005（佣金报酬）</option>
                <option value="1009">1009（采购货款）</option>
                <option value="1010">1010（二手回收）</option>
                <option value="1013">1013（公益补助）</option>
                <option value="1002">1002（行政补贴）</option>
                <option value="1004">1004（保险理赔）</option>
              </select>
              <input className="border rounded px-3 py-2" placeholder="场景备注（可选）" value={newScene.name} onChange={(e)=>setNewScene({ ...newScene, name: e.target.value })} />
              <select className="border rounded px-3 py-2" value={newScene.status} onChange={(e)=>setNewScene({ ...newScene, status: e.target.value as any })}>
                <option value="active">启用</option>
                <option value="disabled">停用</option>
              </select>
              <input className="border rounded px-3 py-2" placeholder="用户收款感知（可选）" value={newScene.userRecvPerception || ''} onChange={(e)=>setNewScene({ ...newScene, userRecvPerception: e.target.value })} />
            </div>
            <div className="mt-2"><button className="bg-wechat-green text-white px-3 py-2 rounded" onClick={async ()=>{ try { await addScene({ sceneId: newScene.sceneId, name: newScene.name || undefined, status: newScene.status, userRecvPerception: newScene.userRecvPerception || undefined }); await fetchScenes(); alert('已保存场景'); } catch { alert('保存失败') } }}>保存/启用场景</button></div>
          </div>
          <div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left">场景ID</th>
                  <th className="px-3 py-2 text-left">备注</th>
                  <th className="px-3 py-2 text-left">状态</th>
                  <th className="px-3 py-2 text-left">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scenes.map(s => (
                  <tr key={String(s.id)}>
                    <td className="px-3 py-2">{s.scene_id}</td>
                    <td className="px-3 py-2">{s.name || '-'}</td>
                    <td className="px-3 py-2">{s.status || '-'}</td>
                    <td className="px-3 py-2 space-x-2">
                      <button className="px-2 py-1 border rounded" onClick={async ()=>{ const ns = s.status === 'active' ? 'disabled' : 'active'; try { await updateScene(s.id, { status: ns }); await fetchScenes(); } catch { alert('更新失败') } }}>{s.status === 'active' ? '停用' : '启用'}</button>
                      <button className="px-2 py-1 border rounded" onClick={async ()=>{ if (!confirm('确认删除该场景？')) return; try { await deleteScene(s.id); await fetchScenes(); } catch { alert('删除失败') } }}>删除</button>
                      <button className="px-2 py-1 border rounded" onClick={()=>{ setEditingSceneId(s.id); setEditInfos((s.reportInfos || []).slice()); setEditPerception(s.user_recv_perception || '') }}>编辑报备信息</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editingSceneId && (
            <div className="mt-4 border border-gray-200 rounded p-3">
              <div className="mb-2 text-sm font-medium">编辑报备信息</div>
              <div className="mb-2">
                <input className="border rounded px-3 py-2 w-full" placeholder="用户收款感知（可选，用于覆盖展示）" value={editPerception} onChange={(e)=>setEditPerception(e.target.value)} />
              </div>
              <div className="mb-2 flex gap-2">
                <button className="px-2 py-1 border rounded" onClick={()=>{ const sc = scenes.find(x=>String(x.id)===String(editingSceneId)); const sid = sc?.scene_id || '1006'; const req = ScenesInfo[sid]?.required?.map(it=>({ info_type: it.type, info_content: it.example })) || []; const any = ScenesInfo[sid]?.anyOf?.[0] ? [{ info_type: ScenesInfo[sid].anyOf![0].type, info_content: ScenesInfo[sid].anyOf![0].example }] : []; setEditInfos([ ...req, ...any ]) }}>填充模板</button>
                <button className="px-2 py-1 border rounded" onClick={()=>setEditInfos(prev=>[...prev, { info_type: '', info_content: '' }])}>新增一项</button>
              </div>
              <div className="space-y-2">
                {editInfos.map((row, idx) => (
                  <div key={`ri-${idx}`} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input className="border rounded px-3 py-2" placeholder="info_type" value={row.info_type} onChange={(e)=>{ const v = e.target.value; setEditInfos(prev=>prev.map((r,i)=> i===idx ? { ...r, info_type: v } : r)) }} />
                    <input className="border rounded px-3 py-2" placeholder="info_content" value={row.info_content} onChange={(e)=>{ const v = e.target.value; setEditInfos(prev=>prev.map((r,i)=> i===idx ? { ...r, info_content: v } : r)) }} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button className="bg-wechat-green text-white px-3 py-2 rounded" onClick={async ()=>{ const sc = scenes.find(x=>String(x.id)===String(editingSceneId)); if (!sc) return; try { await updateScene(editingSceneId as any, { userRecvPerception: editPerception || undefined, reportInfos: editInfos }); await fetchScenes(); setEditingSceneId(null); setEditInfos([]); setEditPerception(''); alert('已保存'); } catch { alert('保存失败') } }}>保存</button>
                <button className="px-3 py-2 border rounded" onClick={()=>{ setEditingSceneId(null); setEditInfos([]) }}>取消</button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg px-6 py-4">
          <h3 className="text-sm font-medium text-purple-900 mb-2">场景报备预览</h3>
          <div className="flex items-center gap-3 mb-3">
            <label className="text-sm text-purple-800">选择转账场景ID</label>
            <select value={sceneId} onChange={(e) => setSceneId(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="1000">1000（现金营销）</option>
              <option value="1011">1011（企业赔付）</option>
              <option value="1005">1005（佣金报酬）</option>
              <option value="1009">1009（采购货款）</option>
              <option value="1010">1010（二手回收）</option>
              <option value="1013">1013（公益补助）</option>
              <option value="1002">1002（行政补贴）</option>
              <option value="1004">1004（保险理赔）</option>
            </select>
          </div>
          <div className="text-sm text-purple-800">
            <div className="mb-2 font-medium">场景说明</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="border border-purple-200 rounded-md px-3 py-2">
                <div className="text-purple-900">默认用户收款感知：{ScenesInfo[sceneId].perception}</div>
                <div className="text-purple-700">说明：{ScenesInfo[sceneId].desc}</div>
              </div>
              {ScenesInfo[sceneId].perceptions && ScenesInfo[sceneId].perceptions!.length > 0 && (
                <div className="border border-purple-200 rounded-md px-3 py-2">
                  <div className="text-purple-900">支持的用户收款感知：{ScenesInfo[sceneId].perceptions!.join('、')}</div>
                </div>
              )}
            </div>
            <div className="mb-2 font-medium">必填字段</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ScenesInfo[sceneId].required.map((it, idx) => (
                <div key={`req-${idx}`} className="border border-purple-200 rounded-md px-3 py-2">
                  <div className="text-purple-900">info_type: {it.type}</div>
                  <div className="text-purple-700">info_content: {it.example}</div>
                  <div className="text-xs text-purple-600 mt-1">要求：必须提供，且与业务真实一致</div>
                </div>
              ))}
            </div>
            {ScenesInfo[sceneId].anyOf && ScenesInfo[sceneId].anyOf!.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 font-medium">任选其一（至少一项）</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {ScenesInfo[sceneId].anyOf!.map((it, idx) => (
                    <div key={`any-${idx}`} className="border border-purple-200 rounded-md px-3 py-2">
                      <div className="text-purple-900">info_type: {it.type}</div>
                      <div className="text-purple-700">info_content: {it.example}</div>
                      <div className="text-xs text-purple-600 mt-1">要求：至少提供其中一项</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 mb-2 font-medium">可选字段</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ScenesInfo[sceneId].optional.map((it, idx) => (
                <div key={`opt-${idx}`} className="border border-purple-200 rounded-md px-3 py-2">
                  <div className="text-purple-900">info_type: {it.type}</div>
                  <div className="text-purple-700">info_content: {it.example}</div>
                  <div className="text-xs text-purple-600 mt-1">建议：提供更完整的报备信息</div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-2 text-xs text-purple-700">实际报备内容需符合微信文档枚举与业务真实场景，系统会在转账时自动填充基础字段。</p>
        </div>
      </main>
    </div>
  )
}
